import React from "react";
import "../styles/SMA.css";
import { Helmet, HelmetProvider } from "react-helmet-async";

import { Link } from "react-router-dom";
const SMA = () => {
  return (
    <div>
      <HelmetProvider>
        <Helmet>
          <title>
            Noninvasive Respiratory Care for Infants with SMA and Congenital
            Myopathies
          </title>
          <meta
            name="description"
            content="Learn how noninvasive respiratory aids such as BiPAP, mechanical insufflation-exsufflation (MI-E), and assisted coughing can prevent tracheostomy and hospitalizations in infants with SMA types 1 & 2 and congenital myopathies."
          />
          <meta
            name="keywords"
            content="SMA type 1, SMA type 2, congenital myopathies, infant respiratory care, BiPAP for infants, MI-E, mechanical insufflation-exsufflation, noninvasive ventilation, pediatric airway management, avoid tracheostomy"
          />
          <meta name="author" content="Millennium Respiratory Care" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta
            property="og:image"
            content="https://doctorbach.com/images/BachJohn.jpg"
          />
          <meta
            property="og:image"
            content="https://doctorbach.com/favicon.png"
          />
          <meta
            property="og:url"
            content="https://doctorbach.com/resources/sma"
          />
          <meta property="og:type" content="article" />
        </Helmet>
      </HelmetProvider>
      <div className="sma-container">
        <h3 style={{ textAlign: "center", paddingLeft: 0 }}>
          Infants and Small Children
        </h3>
        <div className="er-pro">
          <h4>
            Spinal Muscular Atrophy Types 1 and 2 and Congenital Myopathies
          </h4>
          <p>
            Spinal Muscular Atrophy Types 1 and 2 and Congenital Myopathies The
            parents of infants with neuromuscular disorders, most often, infants
            with SMA types 1 and 2, are told that their children will not
            survive infancy. Typically, these children are well until a cold
            causes airway secretions to accumulate in the lungs because of a
            weak cough mechanism. Bacteria multiply in the secretions and the
            child develops pneumonia and respiratory failure. After hospital
            admission, the child is typically intubated (a tube is passed via
            the nose or mouth into the lungs) for ventilatory support and airway
            suctioning. Attempts to remove the tube usually fail because the
            methods used to do so are appropriate for children with primarily
            lung disease rather than muscle weakness. Once extubation has
            failed, the parents are told to either let the child die or to agree
            to tracheotomy (placing a tube permanently in the neck for
            ventilatory support and suctioning). We have found that this is
            usually unnecessary when noninvasive inspiratory and expiratory
            muscle aids are used appropriately.
          </p>
          <p>
            Infants cannot air stack delivered volumes of air or cooperate to
            receive maximal insufflations. All SMA type 1 babies, SMA type 2
            infants and others with infantile neuromuscular weakness who have
            paradoxical chest wall movement (inward movement of the chest when
            the belly goes outward during inspiration), require nocturnal high
            span bi-level positive airway pressure to prevent pectus excavatum
            (chest deformity) and promote lung growth (See{" "}
            <Link
              to="https://pubmed.ncbi.nlm.nih.gov/10767247/"
              style={{ textDecoration: "none", color: "darkblue" }}
            >
              Article 145
            </Link>{" "}
            in the Bibliography). Nocturnal high span bi-level positive airway
            pressure prevents or reverses pectus excavatum. It should be
            introduced to the small child once he or she is asleep. The child
            gradually becomes accustomed to it. Inspiratory pressures should
            begin at 15 cm H2O and be gradually increased to 18 to 20 cm H2O.
            Expiratory pressures should be left at the minimum or 2 cm H2O. In
            addition to nocturnal aid, deep insufflations are provided via
            oral-nasal interface for children over 1 year of age along with
            concomitant abdominal thrusts to prevent abdominal expansion so as
            to direct the air into the upper chest. These insufflations need to
            be timed to the child's inspirations. Children can become
            cooperative with deep insufflation therapy by 14 to 30 months of
            age.
          </p>
          <p>
            We have routinely used MI-E via translaryngeal and tracheostomy
            tubes in children with SMA under 1 year of age. Although we have
            been using approximately the same pressure settings as for adults,
            that is, +40 to -40 cm H2O, its use through the very narrow gauge
            pediatric tubes results in a severe pressure drop off that may not
            permit the generation of optimal exsufflation flows. However, even
            at these probably inadequate pressures secretions are expulsed and
            dSaO2s reversed. It needs to be determined how to compensate the
            pressure drop offs through very narrow tubes.
          </p>
          <p>
            The use of MI-E via the upper airway can be effective for children
            as young as 13 months of age. Children this young can become
            accustomed to MI-E and permit its effective use by not crying or
            closing their throats. Between two and one-half and five years of
            age most children become able to cooperate and cough on queue with
            MI-E and they can then avoid hospitalizations and pneumonias during
            their intercurrent chest infections. Exsufflation timed abdominal
            thrusts are also used for infants.
          </p>
          <p>
            Children to small to cooperate with manually and mechanically
            assisted coughing or those who are not introduced to these methods
            before a cold develops into pneumonia and respiratory failure are
            hospitalized and usually intubated (a catheter is passed via the
            nose or mouth into the lungs for ventilatory support and airway
            secretion evacuation). Often, each time that small children develop
            chest infections until they are old enough to cooperate they need to
            be hospitalized and intubated. Our{" "}
            <Link
              to="/resources/icu"
              style={{ textDecoration: "none", color: "darkblue" }}
            >
              Intensive Care Protocol
            </Link>{" "}
            must be used to return these children home without the need to do a
            surgical tracheostomy. Patients using these methods as outpatients
            can also avoid respiratory complications and hospitalizations.
          </p>
          <p>
            Whether used via the nose or mouth or via invasive indwelling airway
            tubes, routine airway suctioning misses the left lung about 90% of
            the time so that 80% of pneumonias are in the left lung. MI-E via an
            airway tube provides the same exsufflation flows in both left and
            right airways without the discomfort or airway trauma of tracheal
            suctioning and it can be effective when suctioning isn't. Patients
            invariably prefer MI-E to suctioning for comfort and effectiveness
            and they find it less tiring. Deep suctioning, whether via airway
            tube or via the upper airway, can essentially be discontinued for
            most patients.
          </p>
          <p>
            The use of MI-E has permitted us to consistently extubate people
            with neuromuscular disease following general anesthesia despite
            their lack of any ability to breathe on their own, and to manage
            them with noninvasive IPPV. It has also permitted us to avoid
            intubation or to quickly extubate people in acute ventilatory
            failure and with profuse airway secretions due to intercurrent chest
            infections. MI-E in a protocol with manually assisted coughing,
            oximetry feedback, and home use of noninvasive IPPV was shown to
            effectively decrease hospitalizations and respiratory complications
            and mortality for people with neuromuscular diseases. It may not be
            effective if the user cannot cooperate sufficiently to keep the
            airway open, if there is a fixed upper airway obstruction, or if
            upper airway dilator muscles cannot maintain sufficient patency to
            allow for PCF to exceed 160 L/m. This is most often seen in advanced
            amyotrophic lateral sclerosis with severe throat muscle weakness.
            MI-E has been demonstrated to be extremely safe even when used many
            times at pressures of 40 to 60 cm H2O. It is rarely effective for
            people of any age at pressures less than 35 cm H2O.
          </p>
          <p>
            For other techniques that assist respiratory muscle effort including
            the use of oro-nasal interfaces, see{" "}
            <Link
              to="/ourcenters/furtherinformation"
              style={{ textDecoration: "none", color: "darkblue" }}
            >
              Further Information
            </Link>{" "}
            . People with generalized weakness living at home need to be trained
            and equipped to follow the{" "}
            <Link
              to="/resources/op"
              style={{ textDecoration: "none", color: "darkblue" }}
            >
              Outpatient Protocol
            </Link>{" "}
            of respiratory muscle aids to remain free of pneumonia, respiratory
            failure, hospitalization, and tracheostomy and read appropriate
            articles and books.
          </p>
        </div>
      </div>
      <div className="also-see">
        <p className="strong-p">
          <strong>Also see:</strong>
        </p>
        <ul>
          <li>
            {" "}
            <p>
              {" "}
              <Link
                to="/ourcenters/furtherinformation"
                style={{ textDecoration: "none", color: "darkblue" }}
              >
                Further Information
              </Link>{" "}
            </p>
          </li>
          <li>
            {" "}
            <p>
              <Link
                to="/resources/icu"
                style={{ textDecoration: "none", color: "darkblue" }}
              >
                Intensive Care Protocol
              </Link>
            </p>
          </li>
          <li>
            <p>
              {" "}
              <Link
                to="/resources/op"
                style={{ textDecoration: "none", color: "darkblue" }}
              >
                Outpatient Protocol
              </Link>{" "}
            </p>
          </li>
          <li>
            <p>
              <Link
                to="https://pubmed.ncbi.nlm.nih.gov/10767247/"
                style={{ textDecoration: "none", color: "darkblue" }}
              >
                Spinal muscular atrophy type 1: A noninvasive respiratory
                management approach
              </Link>{" "}
            </p>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default SMA;
